import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { doc, getDoc, deleteDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import { fromGel } from "../config/currency";
import "./RenovationDetail.css";

const CATEGORY_GROUPS = {
  construction_foundation: [
    "blocks_bricks",
    "cement",
    "sand",
    "gravel",
    "concrete",
    "rebar_metal",
    "metal_mesh",
    "pipes",
    "waterproofing",
    "thermal_insulation",
    "sound_insulation",
    "roofing",
    "gutters",
    "sandwich_panels",
  ],

  preparation_rough_construction: [
    "drywall_gypsum_board",
    "metal_profiles",
    "putty",
    "plaster",
    "primers",
    "self_leveling_floors",
    "tile_adhesive",
    "insulation_adhesive",
    "construction_chemicals",
    "accessories",
  ],

  engineering_utilities: [
    "electrical",
    "cables",
    "switches",
    "sockets",
    "breakers",
    "electrical_boxes",
    "conduits",
    "plumbing",
    "water_pipes",
    "sewer_pipes",
    "fittings",
    "collectors",
    "faucets",
    "heating",
    "radiators",
    "heating_pipes",
    "heating_equipment",
    "ventilation",
    "ducts",
    "grilles",
    "fans",
  ],

  finishing_interior: [
    "tiles",
    "porcelain_ceramic_tiles",
    "laminate",
    "parquet",
    "linoleum",
    "wallpaper",
    "paint",
    "decorative_plaster",
    "doors",
    "windows",
    "bathroom_fixtures",
    "bathtubs",
    "showers",
    "toilets",
    "sinks",
    "lighting",
  ],
};

const UNIT_KEYS = {
  piece: "renovation.units.piece",
  m2: "renovation.units.m2",
  m3: "renovation.units.m3",
  kg: "renovation.units.kg",
  liter: "renovation.units.liter",
  meter: "renovation.units.meter",
};

export default function RenovationDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [listing, setListing] = React.useState(null);
  const [ownerUser, setOwnerUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const [photoIndex, setPhotoIndex] = React.useState(0);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);

  const [currency, setCurrency] = React.useState(
    localStorage.getItem("currency") || "GEL"
  );

  const [currentUser, setCurrentUser] = React.useState(null);

  React.useEffect(() => {
    const auth = getAuth();

    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });

    return () => unsubscribe();
  }, []);

  React.useEffect(() => {
    const fetchListing = async () => {
      try {
        const docRef = doc(db, "listings", id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          setListing(null);
          return;
        }

        const data = {
          id: docSnap.id,
          ...docSnap.data(),
        };

        // IMPORTANT:
        // Only renovation listings belong on this page.
        if (data.marketplaceType !== "renovation") {
          setListing(null);
          return;
        }

        setListing(data);
      } catch (error) {
        console.error(
          "Error fetching renovation listing:",
          error
        );

        setListing(null);
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [id]);

  React.useEffect(() => {
    if (!listing?.userId) return;

    const fetchOwner = async () => {
      try {
        const userRef = doc(
          db,
          "publicUsers",
          listing.userId
        );

        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          setOwnerUser(userSnap.data());
        }
      } catch (error) {
        console.error(
          "Failed to fetch renovation listing owner:",
          error
        );
      }
    };

    fetchOwner();
  }, [listing]);

  const changeCurrency = (newCurrency) => {
    localStorage.setItem("currency", newCurrency);
    setCurrency(newCurrency);
  };

  const toggleCurrency = () => {
    changeCurrency(
      currency === "GEL" ? "USD" : "GEL"
    );
  };

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
      return;
    }

    navigate(-1);
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      t("delete_Listing")
    );

    if (!confirmed) return;

    try {
      await deleteDoc(
        doc(db, "listings", id)
      );

      navigate("/");
    } catch (error) {
      console.error(
        "Failed to delete renovation listing:",
        error
      );

      alert(
        t("common.error") ||
          "Something went wrong"
      );
    }
  };

  if (loading) {
    return (
      <div className="renovation-detail-loading">
        {t("common.loading") || "Loading..."}
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="renovation-detail-not-found">
        {t("listing.not_found")}
      </div>
    );
  }

  const price = Number(listing.price);

  const displayedPrice =
    Number.isFinite(price) && price > 0
      ? fromGel(price, currency)
      : null;

  const photos = Array.isArray(listing.photos)
    ? listing.photos
    : [];

  const currentPhoto =
    photos.length > 0
      ? photos[photoIndex]
      : null;

  const quantity =
    listing.quantity !== null &&
    listing.quantity !== undefined &&
    listing.quantity !== ""
      ? Number(listing.quantity)
      : null;

  const unitLabel =
    listing.unit
      ? t(
          UNIT_KEYS[listing.unit] ||
            listing.unit
        )
      : "";

  const selectedGroups =
    Array.isArray(listing.categoryGroups)
      ? listing.categoryGroups
      : [];

  const selectedCategories =
    Array.isArray(listing.categories)
      ? listing.categories
      : [];

  const isOwner =
    currentUser &&
    (currentUser.uid === listing.userId ||
      currentUser.uid === listing.userID);

  return (
    <div className="renovation-detail-page">

      {/* =========================
          TOP BAR
      ========================== */}

      <div className="renovation-detail-topbar">

        <button
          className="renovation-back-btn"
          onClick={handleBack}
        >
          ← {t("common.back")}
        </button>

        <button
          className="renovation-currency-btn"
          onClick={toggleCurrency}
        >
          {currency === "GEL"
            ? "₾ GEL"
            : "$ USD"}
        </button>

      </div>

      {/* =========================
          HEADER
      ========================== */}

      <header className="renovation-detail-header">

        <div className="renovation-title-block">

          <h1>
            {listing.title}
          </h1>

          {listing.brand && (
            <div className="renovation-brand">
              {listing.brand}
            </div>
          )}

        </div>

        {displayedPrice !== null && (
          <div className="renovation-price">
            {currency === "GEL"
              ? "₾"
              : "$"}{" "}
            {displayedPrice.toLocaleString()}
          </div>
        )}

      </header>

      {/* =========================
          GALLERY
      ========================== */}

      {currentPhoto && (
        <section className="renovation-gallery">

          <img
            src={currentPhoto}
            alt={listing.title}
            className="renovation-main-image"
            onClick={() =>
              setLightboxOpen(true)
            }
          />

          {photos.length > 1 && (
            <div className="renovation-gallery-dots">

              {photos.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={
                    index === photoIndex
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    setPhotoIndex(index)
                  }
                  aria-label={`Photo ${
                    index + 1
                  }`}
                />
              ))}

            </div>
          )}

        </section>
      )}

      {/* =========================
          LIGHTBOX
      ========================== */}

      {lightboxOpen && currentPhoto && (
        <div
          className="renovation-lightbox"
          onClick={() =>
            setLightboxOpen(false)
          }
        >

          <button
            type="button"
            className="renovation-lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxOpen(false);
            }}
          >
            ✕
          </button>

          {photoIndex > 0 && (
            <button
              type="button"
              className="renovation-lightbox-arrow left"
              onClick={(e) => {
                e.stopPropagation();
                setPhotoIndex(
                  photoIndex - 1
                );
              }}
            >
              ‹
            </button>
          )}

          <img
            src={currentPhoto}
            alt={listing.title}
            className="renovation-lightbox-image"
            onClick={(e) =>
              e.stopPropagation()
            }
          />

          {photoIndex <
            photos.length - 1 && (
            <button
              type="button"
              className="renovation-lightbox-arrow right"
              onClick={(e) => {
                e.stopPropagation();
                setPhotoIndex(
                  photoIndex + 1
                );
              }}
            >
              ›
            </button>
          )}

        </div>
      )}

      {/* =========================
          CATEGORY
      ========================== */}

      {(selectedGroups.length > 0 ||
        selectedCategories.length > 0 ||
        listing.otherCategory) && (
        <section className="renovation-detail-section">

          <h2>
            {t("renovation.category")}
          </h2>

          <div className="renovation-category-content">

            {selectedGroups.map(
              (group) => (
                <div
                  key={group}
                  className="renovation-category-group"
                >

                  <h3>
                    {t(
                      `renovation.groups.${group}`
                    )}
                  </h3>

                  <div className="renovation-category-list">

                    {(
                      CATEGORY_GROUPS[
                        group
                      ] || []
                    )
                      .filter(
                        (category) =>
                          selectedCategories.includes(
                            category
                          )
                      )
                      .map(
                        (category) => (
                          <span
                            key={category}
                            className="renovation-category-tag"
                          >
                            {t(
                              `renovation.categories.${category}`
                            )}
                          </span>
                        )
                      )}

                  </div>

                </div>
              )
            )}

            {listing.otherCategory && (
              <div className="renovation-other-category">

                <strong>
                  {t(
                    "renovation.other"
                  )}
                  :
                </strong>{" "}
                {listing.otherCategory}

              </div>
            )}

          </div>

        </section>
      )}

      {/* =========================
          BASIC DETAILS
      ========================== */}

      <section className="renovation-detail-section">

        <h2>
          {t("renovation.stepDetailsTitle")}
        </h2>

        <div className="renovation-details-grid">

          <Detail
            label={t(
              "renovation.condition"
            )}
            value={
              listing.condition
                ? t(
                    `renovation.${listing.condition}`
                  )
                : null
            }
          />

          <Detail
            label={t(
              "renovation.quantity"
            )}
            value={
              quantity !== null
                ? `${quantity}${
                    unitLabel
                      ? ` ${unitLabel}`
                      : ""
                  }`
                : null
            }
          />

          <Detail
            label={t(
              "renovation.warranty"
            )}
            value={
              listing.warranty
                ? t("common.yes")
                : t("common.no")
            }
          />

        </div>

      </section>

      {/* =========================
          AVAILABILITY
      ========================== */}

      {(listing.storeAvailable ||
        listing.productionAvailable ||
        listing.deliveryAvailable ||
        listing.pickupAvailable) && (

        <section className="renovation-detail-section">

          <h2>
            {t(
              "renovation.availability"
            )}
          </h2>

          <div className="renovation-availability-grid">

            {listing.storeAvailable && (
              <div className="renovation-availability-card">
                <span className="availability-icon">
                  🏪
                </span>

                <span>
                  {t(
                    "renovation.storeAvailable"
                  )}
                </span>
              </div>
            )}

            {listing.productionAvailable && (
              <div className="renovation-availability-card">
                <span className="availability-icon">
                  🏭
                </span>

                <span>
                  {t(
                    "renovation.productionAvailable"
                  )}
                </span>
              </div>
            )}

            {listing.deliveryAvailable && (
              <div className="renovation-availability-card">
                <span className="availability-icon">
                  🚚
                </span>

                <span>
                  {t(
                    "renovation.deliveryAvailable"
                  )}
                </span>
              </div>
            )}

            {listing.pickupAvailable && (
              <div className="renovation-availability-card">
                <span className="availability-icon">
                  📦
                </span>

                <span>
                  {t(
                    "renovation.pickupAvailable"
                  )}
                </span>
              </div>
            )}

          </div>

        </section>
      )}

      {/* =========================
          LOCATIONS
      ========================== */}

      {(listing.storeAddress ||
        listing.productionAddress) && (

        <section className="renovation-detail-section">

          <h2>
            {t(
              "renovation.locations"
            )}
          </h2>

          <div className="renovation-locations">

            {listing.storeAddress && (
              <div className="renovation-location-row">
                <span>
                  🏪
                </span>

                <div>
                  <strong>
                    {t(
                      "renovation.storeLocation"
                    )}
                  </strong>

                  <p>
                    {listing.storeAddress}
                  </p>
                </div>
              </div>
            )}

            {listing.productionAddress && (
              <div className="renovation-location-row">
                <span>
                  🏭
                </span>

                <div>
                  <strong>
                    {t(
                      "renovation.productionLocation"
                    )}
                  </strong>

                  <p>
                    {
                      listing.productionAddress
                    }
                  </p>
                </div>
              </div>
            )}

          </div>

        </section>
      )}

      {/* =========================
          DESCRIPTION
      ========================== */}

      {listing.description && (
        <section className="renovation-detail-section">

          <h2>
            {t(
              "propertaa.description"
            )}
          </h2>

          <p className="renovation-description">
            {listing.description}
          </p>

        </section>
      )}

      {/* =========================
          CONTACT
      ========================== */}

      <section className="renovation-contact-section">

        <div className="renovation-seller-card">

          <div className="renovation-seller-avatar">
            👤
          </div>

          <div className="renovation-seller-info">

            <strong>
              {ownerUser?.username ||
                listing.username ||
                "User"}
            </strong>

            {listing.userId && (
              <button
                type="button"
                className="renovation-user-link"
                onClick={() =>
                  navigate(
                    `/user/${listing.userId}`
                  )
                }
              >
                {t(
                  "listing.viewAllFromUser"
                )}
              </button>
            )}

          </div>

        </div>

        {listing.phone && (
          <a
            href={`tel:${listing.phone}`}
            className="renovation-call-btn"
          >
            📞 {t("call_now")}
          </a>
        )}

      </section>

      {/* =========================
          OWNER ACTIONS
      ========================== */}

      {isOwner && (
        <section className="renovation-owner-actions">

          <button
            type="button"
            className="renovation-edit-btn"
            onClick={() =>
              navigate(
                "/add-renovation-listing",
                {
                  state: {
                    listing,
                    editing: true,
                  },
                }
              )
            }
          >
            {t("edit_listing")}
          </button>

          <button
            type="button"
            className="renovation-delete-btn"
            onClick={handleDelete}
          >
            {t("delete_Listing")}
          </button>

        </section>
      )}

    </div>
  );
}


/* =========================================
   DETAIL COMPONENT
========================================= */

function Detail({ label, value }) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  return (
    <div className="renovation-detail-item">

      <span>
        {label}
      </span>

      <strong>
        {value}
      </strong>

    </div>
  );
}
